import { pageSeo } from "../utils/seoContent";
import { BrandJsonLd, LocalBusinessJsonLd, LogoJsonLd, NextSeo } from "next-seo";
import { motion } from "framer-motion";
import { HeartOutlined, EnvironmentOutlined, StarOutlined, CrownOutlined } from "@ant-design/icons";
import Head from "next/head";
import Link from "next/link";

const AboutUs = () => {
    const values = [
        {
            icon: <EnvironmentOutlined />,
            title: "Sustainability",
            description: "Every piece is crafted with eco-friendly materials and ethical practices, ensuring our fashion choices contribute positively to our planet."
        },
        {
            icon: <StarOutlined />,
            title: "Customization",
            description: "We believe in empowering you to express your individuality through personalized designs that reflect your unique style and preferences."
        },
        {
            icon: <CrownOutlined />,
            title: "Quality",
            description: "Our commitment to excellence ensures that every garment meets the highest standards of craftsmanship and durability."
        },
        {
            icon: <HeartOutlined />,
            title: "Passion",
            description: "Fashion is our passion, and we pour our heart into every design, creating pieces that inspire confidence and joy."
        }
    ];

    return (
        <>
            <Head>
                <title>About Us - Chethi&apos;s</title>
            </Head>
            <NextSeo {...pageSeo.about} />
            <LogoJsonLd {...pageSeo.logoJsonLD} />
            <LocalBusinessJsonLd {...pageSeo.localBusinessJsonLD} />
            <BrandJsonLd {...pageSeo.brandJsonLD} />
            
            <div className="luxury-about">
                {/* Hero Section */}
                <motion.div 
                    className="luxury-about__hero"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                >
                    <div className="luxury-about__hero-content">
                        <motion.h1 
                            className="luxury-about__title"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                        >
                            Our Story
                        </motion.h1>
                        <motion.p 
                            className="luxury-about__subtitle"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                        >
                            Where sustainable luxury meets personal expression
                        </motion.p>
                    </div>
                </motion.div>

                <div className="luxury-about__container">
                    {/* Story Section */}
                    <motion.div 
                        className="luxury-about__story"
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.6 }}
                    >
                        <div className="luxury-about__story-content">
                            <h2 className="luxury-about__story-title">Welcome to Chethi&apos;s</h2>
                            <div className="luxury-about__story-text">
                                <p>
                                    Welcome to Chethi&apos;s, your premier destination for eco-friendly fashion with a personal touch. 
                                    We believe in empowering our customers to express their individuality through sustainable and 
                                    stylish clothing that tells their unique story.
                                </p>
                                <p>
                                    Our curated collection features thoughtfully designed pieces that blend fashion-forward 
                                    aesthetics with environmental responsibility. Each garment represents our commitment to 
                                    creating beautiful fashion that doesn&apos;t compromise on our planet&apos;s wellbeing.
                                </p>
                                <p>
                                    What sets Chethi&apos;s apart is our dedication to customization. We invite you to unleash 
                                    your creativity by personalizing our designs to suit your unique preferences. Whether 
                                    you&apos;re seeking a bespoke outfit or looking to modify an existing piece, our team is 
                                    passionate about bringing your vision to life.
                                </p>
                            </div>
                        </div>
                    </motion.div>

                    {/* Values Section */}
                    <motion.div 
                        className="luxury-about__values"
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.8 }}
                    >
                        <h2 className="luxury-about__values-title">Our Values</h2>
                        <div className="luxury-about__values-grid">
                            {values.map((value, index) => (
                                <motion.div
                                    key={value.title}
                                    className="luxury-about__value"
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 1.0 + index * 0.1 }}
                                    whileHover={{ y: -5, scale: 1.02 }}
                                >
                                    <div className="luxury-about__value-icon">
                                        {value.icon}
                                    </div>
                                    <h3 className="luxury-about__value-title">{value.title}</h3>
                                    <p className="luxury-about__value-description">{value.description}</p>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>

                    {/* Mission Section */}
                    <motion.div 
                        className="luxury-about__mission"
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 1.4 }}
                    >
                        <div className="luxury-about__mission-content">
                            <h2 className="luxury-about__mission-title">Our Mission</h2>
                            <p className="luxury-about__mission-text">
                                Founded on the principles of sustainability and innovation, Chethi&apos;s strives to redefine 
                                fashion by offering a guilt-free shopping experience. We&apos;re committed to creating a fashion 
                                revolution where style meets sustainability, inspiring positive change one outfit at a time.
                            </p>
                            <div className="luxury-about__signature">
                                <div className="luxury-about__signature-line"></div>
                                <p className="luxury-about__signature-text">Chethi&apos;s by Chethana Lakshani</p>
                            </div>
                        </div>
                    </motion.div>

                    {/* Call to Action */}
                    <motion.div 
                        className="luxury-about__cta"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 1.6 }}
                    >
                        <h3 className="luxury-about__cta-title">Ready to Join Our Journey?</h3>
                        <p className="luxury-about__cta-text">
                            Discover the joy of creating your own fashion narrative with Chethi&apos;s. 
                            Let&apos;s inspire change together.
                        </p>
                        <div className="luxury-about__cta-buttons">
                            <Link href="/shop" className="luxury-about__cta-button luxury-about__cta-button--primary">
                                Explore Collection
                            </Link>
                            <Link href="/contact-us" className="luxury-about__cta-button luxury-about__cta-button--secondary">
                                Get in Touch
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </div>
        </>
    );
};

export default AboutUs;
